import { ColumnDef } from '@tanstack/react-table';
import { Eye, Key, PencilSimple } from '@phosphor-icons/react';
import { DataTableColumnHeader } from '@/components/core';
import { Button } from '@/components/ui-kit/button';
import { OrganizationRoleRow } from '../../types/role.types';

interface CreateRoleTableColumnsProps {
  t: (key: string) => string;
  onUsers: (row: OrganizationRoleRow) => void;
  onPermissions: (row: OrganizationRoleRow) => void;
  onEdit: (row: OrganizationRoleRow) => void;
  canViewUsers?: boolean;
  canUpdate?: boolean;
}

/**
 * Read-only roles (Head of Department, workflow-operator) can't be edited here. No role can be
 * deleted from this page.
 */
export const createRoleTableColumns = ({
  t,
  onUsers,
  onPermissions,
  onEdit,
  canViewUsers = false,
  canUpdate = true,
}: CreateRoleTableColumnsProps): ColumnDef<OrganizationRoleRow>[] => [
  {
    id: 'Name',
    meta: { filter: { type: 'text' } },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('ROLE_NAME')} />,
    accessorFn: (row) => row.role.name,
    cell: ({ row }) => (
      // Card: long names (Head of Department: <country> / <department>) wrap beside the actions.
      <span className="whitespace-nowrap font-medium max-md:whitespace-normal">
        {row.original.role.name}
      </span>
    ),
  },
  {
    id: 'Key',
    // Card: full width, as Head of Department keys are long and have no spaces to wrap at.
    meta: { filter: { type: 'text' }, mobile: 'full' },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('ROLE_KEY')} />,
    accessorFn: (row) => row.role.slug,
    cell: ({ row }) => (
      <span className="whitespace-nowrap max-md:whitespace-normal max-md:break-all">
        {row.original.role.slug}
      </span>
    ),
  },
  {
    id: 'Description',
    meta: { mobile: 'full' },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('DESCRIPTION')} />,
    // The widest column; long descriptions wrap to two lines instead of stretching the table.
    size: 480,
    accessorFn: (row) => row.role.description ?? '',
    cell: ({ row }) => {
      const description = row.original.role.description;
      return (
        <span
          className="line-clamp-2 min-w-64 whitespace-normal break-words max-md:min-w-0"
          title={description ?? undefined}
        >
          {description || '-'}
        </span>
      );
    },
  },
  {
    id: 'actions',
    // Card: top right, as the old portal's card header.
    meta: { mobile: 'badge' as const },
    header: () => <span className="text-xs font-medium">{t('ACTION')}</span>,
    enableSorting: false,
    enableHiding: false,
    cell: ({ row }: { row: { original: OrganizationRoleRow } }) => (
      <div className="flex items-center gap-1">
        {canViewUsers && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onUsers(row.original)}
            aria-label={t('ROLE_USERS')}
          >
            <Eye className="size-4" />
          </Button>
        )}
        <Button
          variant="ghost"
          size="icon"
          onClick={() => onPermissions(row.original)}
          aria-label={t('ROLE_PERMISSIONS')}
        >
          <Key className="size-4" />
        </Button>
        {canUpdate && !row.original.readOnly && (
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onEdit(row.original)}
            aria-label={t('EDIT')}
          >
            <PencilSimple className="size-4" />
          </Button>
        )}
      </div>
    ),
  },
];
